import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useAppStore } from "@/store/appStore";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/api/client";
import { getShadow } from "@/components/ui/ShadowBadge";
import { LogoMark } from "@/components/auth/InstitutionSelect";
import { getLevel } from "@/components/ui/XPBar";
import { normalizeIdentity } from "@/lib/studox-auth/index.js";
import clsx from "clsx";

// ─── StudoX App Switcher ──────────────────────────────────────────────────────
const STUDOX_APPS = [
  {
    id:      "flow",
    label:   "StudoX Flow",
    sub:     "Gamified Learning",
    emoji:   "🎓",
    color:   "from-violet-600 to-cyan-500",
    current: true,
    url:     null,
  },
  {
    id:      "core",
    label:   "StudoX Core",
    sub:     "Alle Apps im Überblick",
    emoji:   "⚡",
    color:   "from-violet-600 to-purple-700",
    current: false,
    url:     import.meta.env.VITE_CORE_URL || "https://core.studox.eu",
    useCore: true, // special: use SSO → core.studox.eu (not /auth/callback)
  },
  {
    id:      "clubs",
    label:   "Offline Clubs",
    sub:     "Clubs & Sessions",
    emoji:   "🏛️",
    color:   "from-indigo-600 to-emerald-600",
    current: false,
    url:     import.meta.env.VITE_CLUBS_URL || "https://clubs.studox.eu",
  },
];

function AppSwitcher() {
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(null);
  const ref                     = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const openApp = useCallback(async (app) => {
    if (app.current || loading) return;
    setLoading(app.id);
    setOpen(false);

    // Core Hub — SSO redirect to core.studox.eu?sso=<nonce>
    if (app.useCore) {
      const { launchCore } = await import("@/utils/launchCore");
      await launchCore();
      setLoading(null);
      return;
    }

    try {
      const { data } = await api.post("/auth/sso-token", { app: app.id });
      window.location.href = `${app.url}/auth/callback?sso=${encodeURIComponent(data.token)}`;
    } catch {
      window.location.href = app.url;
    } finally {
      setLoading(null);
    }
  }, [loading]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        title="StudoX Apps"
        className={clsx(
          "relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200",
          "bg-nexus-s2 border border-nexus-border",
          "hover:bg-nexus-s3 hover:border-violet-500/35 hover:text-violet-400 text-gray-400",
          "active:scale-95",
          open && "border-violet-500/50 text-violet-400 bg-nexus-s3"
        )}
      >
        {/* 3×3 dots / apps grid icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5"  cy="5"  r="2"/><circle cx="12" cy="5"  r="2"/><circle cx="19" cy="5"  r="2"/>
          <circle cx="5"  cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
          <circle cx="5"  cy="19" r="2"/><circle cx="12" cy="19" r="2"/><circle cx="19" cy="19" r="2"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-11 w-64 rounded-2xl overflow-hidden z-50 animate-fade-in"
          style={{
            background: "rgba(10,10,30,0.97)",
            border: "1px solid rgba(139,92,246,0.25)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.1)",
            backdropFilter: "blur(24px)",
          }}
        >
          {/* Header */}
          <div className="px-4 pt-3.5 pb-2.5 border-b border-white/[0.06]">
            <p className="text-[10px] font-black uppercase tracking-widest text-violet-400/80">StudoX Apps</p>
            <p className="text-[11px] text-white/30 mt-0.5">Nahtlos zwischen Apps wechseln</p>
          </div>

          {/* App list */}
          <div className="p-2 space-y-1">
            {STUDOX_APPS.map((app) => (
              <button
                key={app.id}
                onClick={() => openApp(app)}
                disabled={app.current || loading === app.id}
                className={clsx(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left",
                  app.current
                    ? "cursor-default opacity-100"
                    : "hover:bg-white/[0.06] active:scale-[0.98] cursor-pointer",
                )}
                style={app.current ? {
                  background: "rgba(139,92,246,0.1)",
                  border: "1px solid rgba(139,92,246,0.2)",
                } : { border: "1px solid transparent" }}
              >
                {/* App icon */}
                <div
                  className={`w-9 h-9 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center flex-shrink-0 text-base`}
                  style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
                >
                  {loading === app.id ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : app.emoji}
                </div>

                {/* App info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-white truncate">{app.label}</p>
                  <p className="text-[10px] text-white/40 truncate">{app.sub}</p>
                </div>

                {/* Status */}
                {app.current ? (
                  <span className="text-[9px] font-black text-violet-400 bg-violet-500/15 px-1.5 py-0.5 rounded-md flex-shrink-0">AKTIV</span>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="rgba(255,255,255,0.25)" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Footer hint */}
          <div className="px-4 pb-3 pt-1">
            <p className="text-[9px] text-white/20 text-center">Eingeloggt als <span className="text-white/40">StudoX Core</span></p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Icon set ───────────────────────────────────────────────────────────────── */
function IcHome()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg>; }
function IcGrid()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>; }
function IcQuiz()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>; }
function IcArena()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>; }
function IcRank()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8M12 17V3M17 8l-5-5-5 5"/></svg>; }
function IcShop()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>; }
function IcVault()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1"/></svg>; }
function IcTrophy()  { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"/><path d="M6 3h12v8a6 6 0 0 1-12 0z"/><path d="M12 17v4M8 21h8"/></svg>; }
function IcMsg()     { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>; }
function IcPerson()  { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function IcSettings(){ return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a1 1 0 0 0-1.41 0l-.7.71a7 7 0 0 0-9.92 9.92l-.7.7a1 1 0 0 0 0 1.41 1 1 0 0 0 1.41 0l.7-.7A7 7 0 0 0 18.36 7.05l.71-.71a1 1 0 0 0 0-1.41z"/></svg>; }
function IcStudents(){ return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>; }
function IcCodes()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>; }
function IcQuizB()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>; }
function IcTasks()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>; }
function IcBell()    { return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>; }

/* ─── Page title map ─────────────────────────────────────────────────────────── */
const TITLES = {
  "/":             { icon: IcHome,     text: "Dashboard",    adminText: "Übersicht",   adminIcon: IcGrid   },
  "/quiz":         { icon: IcQuiz,     text: "Quiz"          },
  "/arena":        { icon: IcArena,    text: "Arena"         },
  "/leaderboard":  { icon: IcRank,     text: "Rangliste"     },
  "/shop":         { icon: IcShop,     text: "Shop"          },
  "/vault":        { icon: IcVault,    text: "Flow Tresor"  },
  "/achievements": { icon: IcTrophy,   text: "Achievements"  },
  "/messages":     { icon: IcMsg,      text: "Nachrichten"   },
  "/profile":      { icon: IcPerson,   text: "Profil"        },
  "/settings":     { icon: IcSettings, text: "Einstellungen" },
  "/tasks":        { icon: IcTasks,    text: "Aufgaben"      },
  "/students":     { icon: IcStudents, text: "Schüler"       },
  "/codes":        { icon: IcCodes,    text: "Zugangscodes"  },
  "/quizzes":      { icon: IcQuizB,    text: "Quiz-Editor"   },
  "/friends":      { icon: IcStudents, text: "Freunde"       },
  "/reports":      { icon: IcTasks,    text: "Meldungen"     },
};

/* ─── Mini stat chip ─────────────────────────────────────────────────────────── */
function StatChip({ icon, value, color }) {
  return (
    <div className={clsx(
      "hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-xl border transition-all duration-200",
      "bg-nexus-s2 border-nexus-border hover:border-violet-500/30",
    )}>
      {icon}
      <span className={clsx("text-xs font-black tabular-nums", color)}>{value}</span>
    </div>
  );
}

export default function TopBar() {
  const { user }              = useAuthStore();
  const { toggleNotifDrawer } = useAppStore();
  const navigate              = useNavigate();
  const loc                   = useLocation();

  const { data: notifData } = useQuery({
    queryKey:       ["notifications"],
    queryFn:        () => api.get("/notifications").then((r) => r.data),
    refetchInterval: 30_000,
  });

  const unread    = notifData?.notifications?.filter((n) => !n.isRead).length || 0;
  const isStudent = user?.role === "student";
  const xp        = user?.studentProfile?.xp || 0;
  const coins     = user?.studentProfile?.coins || 0;
  const level     = getLevel(xp);
  const idn       = normalizeIdentity(user); // rank-aware identity (who am I)

  const entry    = TITLES[loc.pathname];
  const isAdmin  = !isStudent;
  const PageIcon = entry ? (isAdmin && entry.adminIcon ? entry.adminIcon : entry.icon) : IcGrid;
  const pageText = entry ? (isAdmin && entry.adminText ? entry.adminText : entry.text) : "StudoX Flow";

  return (
    <header
      className="sticky top-0 z-40 safe-top"
      style={{
        background: "linear-gradient(180deg, rgba(6,6,26,0.97) 0%, rgba(6,6,26,0.92) 100%)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(139,92,246,0.14)",
      }}
    >
      {/* Bottom edge glow — static base + a slow travelling light */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.3) 30%, rgba(6,182,212,0.2) 70%, transparent 100%)" }}
      />
      <div className="hairline-anim absolute bottom-0 left-0 right-0 h-px pointer-events-none" />

      <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-3">

        {/* Left: Logo + Page Title */}
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            onClick={() => navigate("/")}
            className="flex-shrink-0 hover:scale-105 active:scale-95 transition-transform duration-200"
            style={{ filter: "drop-shadow(0 0 10px rgba(139,92,246,0.6))" }}
          >
            <LogoMark size={30} />
          </button>

          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-gray-500 flex-shrink-0">
              <PageIcon />
            </span>
            <span className="font-black text-sm text-white tracking-tight truncate">{pageText}</span>
          </div>
        </div>

        {/* Right: Stats + Bell + Avatar */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isStudent && user?.studentProfile && (
            <>
              <StatChip
                icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M9 9h4.5a1.5 1.5 0 0 1 0 3H9m0 3h5.5a1.5 1.5 0 0 0 0-3"/></svg>}
                value={coins.toLocaleString("de-DE")}
                color="text-yellow-400"
              />
              <StatChip
                icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}
                value={`${xp} XP`}
                color="text-violet-400"
              />
              {(user?.studentProfile?.streak || 0) > 0 && (
                <StatChip
                  icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#fb923c" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>}
                  value={user.studentProfile.streak}
                  color="text-orange-400"
                />
              )}
            </>
          )}

          {/* Rank identity chip — "who am I" (rank-aware, system-wide) */}
          {idn && (
            <span
              className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] font-black whitespace-nowrap"
              style={{ background: `linear-gradient(135deg, ${idn.rank.color}, ${idn.rank.color2})`, color: "#fff", boxShadow: `0 0 12px ${idn.rank.color}44` }}
              title={`Du bist: ${idn.name} · ${idn.rank.title}${idn.verified ? " · verifiziert" : ""}`}
            >
              <span>{idn.rank.glyph}</span>{idn.rank.title}{idn.verified && <span style={{ color: "rgba(255,255,255,0.9)" }}>✓</span>}
            </span>
          )}

          {/* App Switcher */}
          <AppSwitcher />

          {/* Notification bell */}
          <button
            onClick={toggleNotifDrawer}
            className={clsx(
              "relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200",
              "bg-nexus-s2 border border-nexus-border",
              "hover:bg-nexus-s3 hover:border-violet-500/35 hover:text-white text-gray-400",
              "active:scale-95"
            )}
          >
            <IcBell />
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 w-[18px] h-[18px] bg-red-500 rounded-full text-[9px] font-black text-white flex items-center justify-center"
                style={{ boxShadow: "0 0 10px rgba(239,68,68,0.6)", animation: "warp-pulse 2s ease-out infinite" }}
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>

          {/* Settings */}
          <button
            onClick={() => navigate("/settings")}
            title="Einstellungen"
            className={clsx(
              "relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200",
              "bg-nexus-s2 border border-nexus-border",
              "hover:bg-nexus-s3 hover:border-violet-500/35 hover:text-violet-400 text-gray-400",
              "active:scale-95",
              loc.pathname === "/settings" && "border-violet-500/50 text-violet-400"
            )}
          >
            <IcSettings />
          </button>

          {/* Avatar */}
          <button
            onClick={() => navigate(user?.role === "admin" ? "/" : "/profile")}
            className={clsx(
              "relative w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden",
              "bg-nexus-s2 border border-nexus-border",
              "hover:bg-nexus-s3 hover:border-violet-500/40 transition-all duration-200",
              "active:scale-95"
            )}
          >
            {user?.avatar && user.avatar.length <= 2 ? (
              <span className="text-base leading-none">{user.avatar}</span>
            ) : (
              <span className="text-gray-400"><IcPerson /></span>
            )}
            {/* Level badge */}
            {isStudent && (
              <span
                className="absolute -bottom-0.5 -right-0.5 text-[8px] font-black px-1 py-0.5 rounded-md leading-none"
                style={{ background: "linear-gradient(135deg, #8b5cf6, #06b6d4)", color: "#fff" }}
              >
                {level}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
